import { useState } from "react";
import { CheckmarkRegular, ChevronDown20Regular } from "@fluentui/react-icons";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * LanguageSwitch — Sprache eines Besuchers.
 *
 * Das Sprachkürzel in der Kopfzeile der Besucherkarte öffnet ein kleines
 * Dropdown; die aktive Sprache trägt dort ein Häkchen. Der Trigger ist das
 * Kürzel selbst — ein zusätzliches Symbol wäre nur Wiederholung, die Sprache
 * steht ja bereits da.
 *
 * Angeboten werden nur Sprachen, für die es auch eine E-Mail-Vorlage gibt
 * (plus die Standardsprache) — das filtert `buildLanguageOptions()`. Bei nur
 * einer Sprache rendert die Komponente nichts: dann gibt es nichts zu
 * entscheiden.
 */
export function LanguageSwitch({ value, options = [], onChange, disabled = false }) {
  const [open, setOpen] = useState(false);

  if (options.length < 2) return null;

  // Ohne gesetzten Wert greift serverseitig die Standardsprache — also steht
  // sie auch hier.
  const active =
    options.find((o) => o.value === value) ||
    options.find((o) => o.isDefault) ||
    options[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`Sprache: ${active.label}`}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center gap-[0.2rem] shrink-0",
            "h-[3rem] px-[0.7rem] rounded-btn border border-transparent",
            "text-[1.15rem] font-semibold font-[inherit] tracking-[0.02em]",
            "transition-all duration-100 cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            disabled
              ? "cursor-not-allowed opacity-60 text-dim"
              : "text-dim hover:bg-[#f4f4f5] hover:border-gray hover:text-foreground",
            open && "bg-[#f4f4f5] border-gray text-foreground",
          )}
        >
          {active.short}
          <ChevronDown20Regular
            className={cn(
              "w-[1.2rem] h-[1.2rem] transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="p-[0.4rem] w-auto min-w-[14rem]">
        {options.map((option) => {
          const isActive = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(option.value);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-[0.8rem] px-[1rem] py-[0.7rem]",
                "rounded-[0.6rem] text-[1.25rem] text-left font-[inherit]",
                "border-none cursor-pointer transition-colors",
                isActive ? "bg-[#fefce8]" : "bg-transparent hover:bg-[#f4f4f5]",
              )}
            >
              <span className="w-[1.4rem] shrink-0 text-[#7a8200]">
                {isActive && <CheckmarkRegular className="w-[1.4rem] h-[1.4rem]" />}
              </span>
              <span className="flex-1 truncate text-foreground">{option.label}</span>
              <span className="text-[1.1rem] font-semibold text-dim">
                {option.short}
              </span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
